export const LANGUAGES = ['es', 'en', 'de'] as const;
export type StoreLanguage = typeof LANGUAGES[number];
export type Messages = Record<string, string>;
export const LANGUAGE_LABELS = { es: 'Español', en: 'English', de: 'Deutsch' } as const;
export const LANGUAGE_STORAGE_KEY = 'solsazon.language';
export const LANGUAGE_TAGS = { es: 'es-ES', en: 'en-GB', de: 'de-DE' } as const;

export function isLanguage(value: unknown): value is StoreLanguage {
  return LANGUAGES.includes(value as StoreLanguage);
}
export function splitLocalePath(path: string): { language: StoreLanguage; pathname: string } {
  const match = path.match(/^\/(en|de)(?=\/|[?#]|$)/);
  const rest = match ? path.slice(match[0].length) : '';
  return match ? { language: match[1] as StoreLanguage, pathname: /^[?#]/.test(rest) ? '/' + rest : rest || '/' }
    : { language: 'es', pathname: path || '/' };
}
export function localizedPath(path: string, language: StoreLanguage): string {
  if (!path.startsWith('/') || path.startsWith('//')) return path;
  const withoutLocale = splitLocalePath(path).pathname;
  if (/^\/(admin|api|assets|images|storage|\.well-known)(?:\/|[?#]|$)|^\/(?:robots\.txt|favicon[^/]*|sitemap[^/]*\.xml)(?:[?#]|$)/.test(withoutLocale)) return withoutLocale;
  if (/\.(?:png|jpe?g|webp|svg|ico|gif|css|m?js|map|json|txt|xml|webmanifest|pdf)(?:[?#]|$)/i.test(withoutLocale)) return withoutLocale;
  if (language === 'es') return withoutLocale;
  return '/' + language + (withoutLocale === '/' ? '' : withoutLocale);
}
export function localizedUrl(url: string, language: StoreLanguage): string {
  if (url.startsWith('/')) return localizedPath(url, language);
  try {
    const parsed = new URL(url);
    if (!['www.sal-sazon.com', 'sal-sazon.com'].includes(parsed.hostname)) return url;
    if (/^#(?:website|organization|logo|person)/.test(parsed.hash) && parsed.pathname === '/') return url;
    return parsed.origin + localizedPath(parsed.pathname, language) + parsed.search + parsed.hash;
  } catch { return url; }
}
export function normalizeMessage(value: string): string { return value.replace(/\s+/g, ' ').trim(); }

const patternCache = new WeakMap<Messages, Array<{ source: RegExp; target: string; tokens: string[] }>>();
function patterns(messages: Messages) {
  let result = patternCache.get(messages);
  if (result) return result;
  result = [];
  for (const [source, target] of Object.entries(messages)) {
    const tokens = source.match(/\{\{v\d+\}\}/g);
    if (!tokens?.length || tokens.length > 4 || source.replace(/\{\{v\d+\}\}/g, '').trim().length < 4) continue;
    const pieces = source.split(/\{\{v\d+\}\}/g).map(piece => piece.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    result.push({ source: new RegExp('^' + pieces.join('(.+?)') + '$'), target, tokens });
  }
  patternCache.set(messages, result);
  return result;
}
/** Translate display values only. IDs, filters, form values and stored data stay intact. */
export function translateDisplay<T>(value: T, messages: Messages, language: StoreLanguage, depth = 0): T {
  if (language === 'es') return value;
  if (Array.isArray(value)) return value.map(item => translateDisplay(item, messages, language, depth)) as T;
  if (typeof value !== 'string') return value;
  const key = normalizeMessage(value);
  if (!key || /^https?:\/\/|^[^\s@]+@[^\s@]+$/.test(key) || /^[A-Z][A-Z0-9_-]*\d[A-Z0-9_-]*$/.test(key)) return value;
  const leading = value.match(/^\s*/)?.[0] || '';
  const trailing = value.match(/\s*$/)?.[0] || '';
  if (Object.hasOwn(messages, key) && messages[key]) return (leading + messages[key] + trailing) as T;
  if (depth < 2 && key.length < 4096) {
    for (const pattern of patterns(messages)) {
      const match = key.match(pattern.source);
      if (match) return (leading + pattern.target.replace(/\{\{v(\d+)\}\}/g, (_, n) =>
        String(translateDisplay(match[pattern.tokens.indexOf('{{v' + n + '}}') + 1] ?? '', messages, language, depth + 1))) + trailing) as T;
    }
  }
  return value;
}
export function localizeSchema(value: unknown, messages: Messages, language: StoreLanguage, field = ''): any {
  if (language === 'es') return value;
  if (Array.isArray(value)) return value.map(item => localizeSchema(item, messages, language, field));
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, localizeSchema(item, messages, language, key)]));
  if (typeof value !== 'string') return value;
  if (field === 'inLanguage') return LANGUAGE_TAGS[language];
  if (['url', '@id', 'item', 'mainEntityOfPage', 'isPartOf'].includes(field)) return localizedUrl(value, language);
  if (['name', 'headline', 'description', 'caption', 'text', 'recipeIngredient'].includes(field)) return translateDisplay(value, messages, language);
  return value;
}

function decodeHtmlText(text: string): string {
  const named: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };
  return text.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos|nbsp);/gi, (entity, key) => {
    if (key[0] !== '#') return named[key.toLowerCase()] || entity;
    const value = key[1].toLowerCase() === 'x' ? parseInt(key.slice(2), 16) : Number(key.slice(1));
    return Number.isInteger(value) && value >= 0 && value <= 0x10ffff ? String.fromCodePoint(value) : entity;
  });
}
function escapeHtmlText(text: string): string {
  return text.replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]!);
}
/** Localize an already escaped, application-generated HTML fragment without adding markup. */
export function localizeHtml(html: string, messages: Messages, language: StoreLanguage): string {
  if (language === 'es') return html;
  return html.split(/(<[^>]*>)/g).map(part => {
    if (part.startsWith('<')) return part.replace(/\b(href|alt|title|aria-label)="([^"]*)"/g, (_, attribute, value) => {
      const decoded = decodeHtmlText(value);
      const localized = attribute === 'href' ? localizedUrl(decoded, language) : translateDisplay(decoded, messages, language);
      return attribute + '="' + escapeHtmlText(localized) + '"';
    });
    const decoded = decodeHtmlText(part);
    const localized = translateDisplay(decoded, messages, language);
    return localized === decoded ? part : escapeHtmlText(localized);
  }).join('');
}

/** Format public ISO and legacy Spanish labels at display time, without altering stored values. */
export function formatDateLabel(value: string, language: StoreLanguage): string {
  const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  const legacy = value.trim().toLowerCase().match(/^(\d{1,2}) de ([a-z]+) de (\d{4})$/);
  let date: Date;
  if (legacy && months.includes(legacy[2])) date = new Date(Date.UTC(Number(legacy[3]), months.indexOf(legacy[2]), Number(legacy[1])));
  else if (/^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)) date = new Date(value);
  else return value;
  if (!Number.isFinite(date.getTime())) return value;
  return new Intl.DateTimeFormat(LANGUAGE_TAGS[language], { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
}
